// Galaxy starfield: twinkling, drifting stars and shooting stars, all drawn on
// one <canvas class="galaxy__canvas"> (styled in scss/layout/_galaxy.scss).
//
// Ported from PUBLIC js/stars.js (the master version, 2026-09-27) with the
// same look and numbers. Changes: it is a module (createGalaxy returns warp()
// and destroy(), so React can mount and unmount it), and it follows the
// canvas's own size (ResizeObserver) rather than the window's, so it also
// works inside a contained box.
//
//   import { createGalaxy } from '.../js/galaxy.js';
//   const galaxy = createGalaxy(canvas);   // galaxy.warp(1); galaxy.destroy();
//
// Options, for pictures of it (the games' Discord cards, screenshotted):
//   seed    the same sky every time (any integer); unseeded skies are random
//   still   one frame, drawn at once and again on resize: no animation, no
//           shooting stars, each star at its own brightness
//
// Also warps on window 'galaxy:warp' events ({ detail: { direction: ±1 } }),
// which PUBLIC's nav.js dispatches when the mobile nav opens and closes.

const CONFIG = {
  starCount: 250,
  minStarSize: 1,
  maxStarSize: 3.2,
  minTwinkleDuration: 2,
  maxTwinkleDuration: 6,
  minDriftDuration: 90,
  maxDriftDuration: 160,

  // Shooting stars
  shootingStarMinInterval: 5000, // ms
  shootingStarMaxInterval: 16000, // ms
  shootingStarMinDuration: 900,
  shootingStarMaxDuration: 1800,
  shootingStarLength: 180, // px

  // Warp: the sky streams past when the mobile nav opens
  warpDistance: 0.6, // of canvas height, travelled by the nearest stars
  warpDuration: 1300, // ms
  warpStreak: 28, // px of streak per px/ms of speed

  // Canvas
  maxPixelRatio: 1, // caps the backing store: 1920x1080 is ~8MB at 1, ~33MB at 2
  idleFps: 30, // frame cap while only the slow twinkle/drift is on screen

  // Overall star look
  starScale: 0.8, // drawn size multiplier
  starOpacity: 0.85, // peak opacity multiplier
};

/** A seeded random number generator (mulberry32): the same numbers in [0, 1) for the same seed. */
const seededRandom = (seed) => {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

/* ------------------ EASING ------------------ */
// CSS cubic-bezier timing functions, precomputed into lookup tables.
const bezier = (a, b, t) => 3 * a * (1 - t) * (1 - t) * t + 3 * b * (1 - t) * t * t + t * t * t;

const makeEasing = (x1, y1, x2, y2) => {
  const N = 256;
  const lut = new Float32Array(N + 1);
  for (let i = 0; i <= N; i++) {
    const x = i / N;
    let lo = 0;
    let hi = 1;
    let t = x;
    for (let k = 0; k < 20; k++) {
      t = (lo + hi) / 2;
      if (bezier(x1, x2, t) < x) lo = t;
      else hi = t;
    }
    lut[i] = bezier(y1, y2, t);
  }
  return (p) => lut[Math.round(p * N)];
};

const easeInOut = makeEasing(0.42, 0, 0.58, 1);
const easeOut = makeEasing(0, 0, 0.58, 1);

/* ------------------ SPRITES ------------------ */
// Every star is one scaled/faded copy of this sprite, so gradients and glow
// are rasterised once rather than per star per frame.
//
// The sprite is a reference-sized star drawn at SPRITE_RES px per CSS px: a
// radial-gradient core plus two soft glows. Canvas shadowBlur uses the same
// Gaussian as CSS box-shadow, so the blur values read like CSS ones.
const SPRITE_RES = 8;
const SPRITE_STAR_SIZE = 2; // CSS px, close to the average star size
const SPRITE_GLOW = 6; // CSS px, the widest box-shadow blur
const SPRITE_RATIO = (SPRITE_STAR_SIZE + SPRITE_GLOW * 2) / SPRITE_STAR_SIZE;

const createStarSprite = () => {
  const size = SPRITE_STAR_SIZE * SPRITE_RATIO * SPRITE_RES;
  const mid = size / 2;
  const r = (SPRITE_STAR_SIZE / 2) * SPRITE_RES;
  const c = document.createElement('canvas');
  c.width = c.height = size;
  const g = c.getContext('2d');

  // box-shadow: 0 0 3px rgba(255,255,255,.9), 0 0 6px rgba(187,222,251,.5)
  // Outer box-shadows aren't painted under the element, so clip the dot out,
  // then draw a disc off-canvas and offset only its shadow back into place.
  g.save();
  g.beginPath();
  g.rect(0, 0, size, size);
  g.arc(mid, mid, r, 0, Math.PI * 2);
  g.clip('evenodd');
  g.fillStyle = '#000';
  g.shadowOffsetX = size * 2;
  [
    [6, 'rgba(187, 222, 251, 0.5)'], // wider, fainter glow painted first
    [3, 'rgba(255, 255, 255, 0.9)'],
  ].forEach(([blur, color]) => {
    g.shadowBlur = blur * SPRITE_RES;
    g.shadowColor = color;
    g.beginPath();
    g.arc(mid - size * 2, mid, r, 0, Math.PI * 2);
    g.fill();
  });
  g.restore();

  // background: radial-gradient(circle, #fff 0, #bbdefb 40%, transparent 70%)
  // "circle" defaults to farthest-corner: half the diagonal of the box.
  const grad = g.createRadialGradient(mid, mid, 0, mid, mid, r * Math.SQRT2);
  grad.addColorStop(0, 'rgba(255, 255, 255, 1)');
  grad.addColorStop(0.4, 'rgba(187, 222, 251, 1)');
  grad.addColorStop(0.7, 'rgba(187, 222, 251, 0)');
  g.fillStyle = grad;
  g.beginPath();
  g.arc(mid, mid, r, 0, Math.PI * 2); // border-radius: 50%
  g.fill();
  return c;
};

const createTrailSprite = () => {
  const c = document.createElement('canvas');
  c.width = CONFIG.shootingStarLength;
  c.height = 4;
  const g = c.getContext('2d');
  const grad = g.createLinearGradient(0, 0, c.width, 0);
  grad.addColorStop(0, 'rgba(255, 255, 255, 0)');
  grad.addColorStop(0.5, 'rgba(255, 255, 255, 0.4)');
  grad.addColorStop(1, 'rgba(255, 255, 255, 0.95)');
  g.filter = 'blur(0.4px)';
  g.fillStyle = grad;
  g.fillRect(0, 1, c.width, 2);
  return c;
};

/* ------------------ STAR LAYOUT ------------------ */
// Stars live in one flat Float32Array (8 floats each) instead of objects.
const X = 0; // 0..1 of canvas width
const Y = 1; // 0..1 of canvas height
const SIZE = 2; // px
const TWINKLE_DUR = 3; // ms
const TWINKLE_OFFSET = 4; // ms into the cycle at start
const DRIFT_X = 5; // px
const DRIFT_Y = 6; // px
const DRIFT_DUR = 7; // ms
const STRIDE = 8;

/**
 * Starts the starfield on a canvas. Returns controls for the running instance.
 * @param {HTMLCanvasElement} canvas
 * @param {{ seed?: number, still?: boolean }} [options]
 * @returns {{ warp(direction: 1 | -1): void, destroy(): void }}
 */
export function createGalaxy(canvas, options = {}) {
  const random = options.seed === undefined ? Math.random : seededRandom(options.seed);
  const randomRange = (min, max) => random() * (max - min) + min;
  const randomSign = () => (random() > 0.5 ? 1 : -1);
  const pictured = Boolean(options.still);

  const ctx = canvas.getContext('2d');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const starSprite = createStarSprite();
  const trailSprite = createTrailSprite();

  const stars = new Float32Array(CONFIG.starCount * STRIDE);

  const createStars = () => {
    for (let i = 0; i < stars.length; i += STRIDE) {
      stars[i + X] = random();
      stars[i + Y] = random();
      stars[i + SIZE] = randomRange(CONFIG.minStarSize, CONFIG.maxStarSize);
      stars[i + TWINKLE_DUR] = randomRange(CONFIG.minTwinkleDuration, CONFIG.maxTwinkleDuration) * 1000;
      // Start each star part-way through its twinkle so the sky is full at once
      stars[i + TWINKLE_OFFSET] = random() * stars[i + TWINKLE_DUR];
      stars[i + DRIFT_X] = randomSign() * randomRange(20, 80);
      stars[i + DRIFT_Y] = randomSign() * randomRange(10, 60);
      stars[i + DRIFT_DUR] = randomRange(CONFIG.minDriftDuration, CONFIG.maxDriftDuration) * 1000;
    }
  };

  // Twinkle keyframes (0% / 40% / 60% / 100%), each segment eased separately.
  // Writes into shared variables to avoid allocating per star per frame.
  let twinkleAlpha = 0;
  let twinkleScale = 1;

  const twinkle = (p) => {
    if (p < 0.4) {
      const e = easeInOut(p / 0.4);
      twinkleAlpha = e;
      twinkleScale = 0.7 + 0.6 * e;
    } else if (p < 0.6) {
      const e = easeInOut((p - 0.4) / 0.2);
      twinkleAlpha = 1 - 0.2 * e;
      twinkleScale = 1.3 - 0.3 * e;
    } else {
      const e = easeInOut((p - 0.6) / 0.4);
      twinkleAlpha = 0.8 - 0.8 * e;
      twinkleScale = 1 - 0.3 * e;
    }
  };

  /* ------------------ SHOOTING STARS ------------------ */
  const shooting = [];

  const createShootingStar = (now) => {
    const angle = (randomRange(18, 35) * Math.PI) / 180;
    shooting.push({
      // Head of the trail, which the streak rotates around
      x: random() * width + CONFIG.shootingStarLength,
      y: (randomRange(5, 55) / 100) * height + 1,
      cos: Math.cos(angle),
      sin: Math.sin(angle),
      distance: randomRange(420, 720),
      start: now,
      duration: randomRange(CONFIG.shootingStarMinDuration, CONFIG.shootingStarMaxDuration),
    });
  };

  const drawShootingStar = (s, p) => {
    // Keyframes: fade and stretch in over the first 10%, then fly and fade out
    let alpha;
    let tx;
    let sx;
    if (p < 0.1) {
      const e = easeOut(p / 0.1);
      alpha = e;
      tx = 0;
      sx = 0.2 + 0.8 * e;
    } else {
      const e = easeOut((p - 0.1) / 0.9);
      alpha = 1 - e;
      tx = s.distance * e;
      sx = 1 - 0.1 * e;
    }

    // rotate(angle) translateX(tx) scaleX(sx) about the head, in one matrix
    ctx.setTransform(
      dpr * s.cos * sx,
      dpr * s.sin * sx,
      -dpr * s.sin,
      dpr * s.cos,
      dpr * (s.x + s.cos * tx),
      dpr * (s.y + s.sin * tx),
    );
    ctx.globalAlpha = alpha;
    ctx.drawImage(trailSprite, -CONFIG.shootingStarLength, -2);
  };

  /* ------------------ WARP ------------------ */
  // One shared vertical offset for the whole sky, eased from its current value
  // to a new one. Each star moves by it scaled to its size (bigger reads as
  // nearer, so it passes faster) and wraps around the canvas, so the sky
  // never runs out. Speed stretches stars into streaks.
  const warpState = { from: 0, to: 0, start: -Infinity, offset: 0, speed: 0 };

  const warpActive = (now) => now - warpState.start < CONFIG.warpDuration;

  const updateWarp = (now) => {
    const p = Math.min((now - warpState.start) / CONFIG.warpDuration, 1);
    const e = easeInOut(p);
    const h = 1 / 64; // finite-difference step for the eased speed
    const distance = warpState.to - warpState.from;
    warpState.offset = warpState.from + distance * e;
    warpState.speed =
      p < 1 ? (Math.abs(distance) * (easeInOut(Math.min(p + h, 1)) - e)) / (h * CONFIG.warpDuration) : 0; // px/ms
  };

  const warp = (direction) => {
    if (pictured || reducedMotion.matches) return;
    const now = performance.now();
    updateWarp(now);
    warpState.from = warpState.offset;
    warpState.to = warpState.offset + direction * CONFIG.warpDistance * height;
    warpState.start = now;
  };

  /* ------------------ DRAWING ------------------ */
  let width = 0;
  let height = 0;
  let dpr = 1;
  const origin = performance.now();

  const draw = (now, still) => {
    const t = now - origin;

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);

    updateWarp(now);
    const streakDir = warpState.to >= warpState.from ? 1 : -1; // streaks trail behind
    const wrapPad = 80; // px beyond each edge, past the widest drift
    const wrapSpan = height + wrapPad * 2;

    for (let i = 0; i < stars.length; i += STRIDE) {
      let drift = 0;

      if (pictured) {
        // Each star where its twinkle starts, never quite out, so the picture looks like the sky.
        twinkle(stars[i + TWINKLE_OFFSET] / stars[i + TWINKLE_DUR]);
        twinkleAlpha = 0.3 + 0.7 * twinkleAlpha;
      } else if (still) {
        twinkleAlpha = 0.8;
        twinkleScale = 1;
      } else {
        const local = t + stars[i + TWINKLE_OFFSET];
        const dur = stars[i + TWINKLE_DUR];
        twinkle((local % dur) / dur);
        if (twinkleAlpha < 0.01) continue;

        // animation-direction: alternate, linear
        drift = (t / stars[i + DRIFT_DUR]) % 2;
        if (drift > 1) drift = 2 - drift;
      }

      const size = stars[i + SIZE];
      const d = size * SPRITE_RATIO * twinkleScale * CONFIG.starScale;
      const depth = size / CONFIG.maxStarSize;
      const cx = stars[i + X] * width + size / 2 + stars[i + DRIFT_X] * drift;
      let cy = stars[i + Y] * height + size / 2 + stars[i + DRIFT_Y] * drift + warpState.offset * depth;
      cy = ((((cy + wrapPad) % wrapSpan) + wrapSpan) % wrapSpan) - wrapPad;

      const streak = warpState.speed * depth * CONFIG.warpStreak;
      const top = cy - d / 2 - (streakDir > 0 ? streak : 0);

      ctx.globalAlpha = twinkleAlpha * CONFIG.starOpacity;
      ctx.drawImage(starSprite, cx - d / 2, top, d, d + streak);
    }

    for (let i = shooting.length - 1; i >= 0; i--) {
      const s = shooting[i];
      const p = (now - s.start) / s.duration;
      if (p >= 1) {
        shooting[i] = shooting[shooting.length - 1];
        shooting.pop();
        continue;
      }
      drawShootingStar(s, p);
    }

    ctx.globalAlpha = 1;
  };

  /* ------------------ LOOP ------------------ */
  // requestAnimationFrame already pauses in background tabs, so a hidden page
  // costs nothing. While visible, frames are capped at idleFps unless a
  // shooting star or warp is moving, which needs the full refresh rate to
  // look smooth.
  const idleFrame = 1000 / CONFIG.idleFps;
  let lastDraw = -Infinity;
  let rafId = 0;

  const nextShootingStarDelay = () => randomRange(CONFIG.shootingStarMinInterval, CONFIG.shootingStarMaxInterval);

  let nextShootAt = origin + nextShootingStarDelay();

  const frame = (now) => {
    rafId = requestAnimationFrame(frame);

    if (now >= nextShootAt) {
      createShootingStar(now);
      nextShootAt = now + nextShootingStarDelay();
    }

    if (!shooting.length && !warpActive(now) && now - lastDraw < idleFrame - 1) return;
    lastDraw = now;
    draw(now, false);
  };

  const start = () => {
    cancelAnimationFrame(rafId);
    shooting.length = 0;
    if (pictured || reducedMotion.matches) {
      draw(performance.now(), true);
    } else {
      lastDraw = -Infinity;
      rafId = requestAnimationFrame(frame);
    }
  };

  const resize = () => {
    // A picture is drawn once, so it can be as sharp as the screen.
    dpr = pictured ? window.devicePixelRatio || 1 : Math.min(window.devicePixelRatio || 1, CONFIG.maxPixelRatio);
    width = canvas.clientWidth;
    height = canvas.clientHeight;
    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);
    // Resizing clears the canvas; repaint now rather than waiting for a frame
    draw(performance.now(), pictured || reducedMotion.matches);
  };

  /* ------------------ INIT ------------------ */
  const onWarpEvent = (e) => warp(e.detail.direction);
  const resizeObserver = new ResizeObserver(resize);

  createStars();
  resize();
  resizeObserver.observe(canvas);
  window.addEventListener('galaxy:warp', onWarpEvent);
  reducedMotion.addEventListener('change', start);
  start();

  return {
    warp,
    destroy() {
      cancelAnimationFrame(rafId);
      resizeObserver.disconnect();
      window.removeEventListener('galaxy:warp', onWarpEvent);
      reducedMotion.removeEventListener('change', start);
    },
  };
}
